import { Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

export function ReconciliationFilterSelect({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string; count?: number }>;
}) {
  return <TextField select SelectProps={{ native: true }} size="small" label={label}
    InputLabelProps={{ shrink: true }} value={value} onChange={(event) => onChange(event.target.value)}
    sx={{ minWidth: 180, maxWidth: '100%' }}>
    {options.map((option) => <option key={option.value} value={option.value}>
      {option.label}{option.count == null ? '' : ` (${option.count})`}
    </option>)}
  </TextField>;
}

export function ReconciliationComparisonFilters<Row, Filter extends string>({
  rows, filters, filter, onFilterChange, onlySyncable, onSyncableChange, matches, canSync, filterLabel,
}: {
  rows: readonly Row[];
  filters: readonly Filter[];
  filter: Filter | '';
  onFilterChange: (value: Filter | '') => void;
  onlySyncable: boolean;
  onSyncableChange: (value: boolean) => void;
  matches: (row: Row, filter: Filter) => boolean;
  canSync: (row: Row) => boolean;
  filterLabel: (filter: Filter) => string;
}) {
  const { t } = useTranslation();
  const eligibleRows = rows.filter((row) => !onlySyncable || canSync(row));
  const categoryRows = rows.filter((row) => !filter || matches(row, filter));
  return <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ my: 2 }}>
    <ReconciliationFilterSelect label={t('reconciliationFilters.syncFilter')}
      value={onlySyncable ? 'syncable' : ''} onChange={(value) => onSyncableChange(value === 'syncable')}
      options={[
        { value: '', label: t('reconciliationFilters.all'), count: categoryRows.length },
        { value: 'syncable', label: t('reconciliationFilters.canSync'), count: categoryRows.filter(canSync).length },
      ]} />
    <ReconciliationFilterSelect label={t('reconciliationFilters.categoryFilter')} value={filter}
      onChange={(value) => {
        const selected = filters.find((candidate) => candidate === value);
        onFilterChange(selected ?? '');
      }}
      options={[
        { value: '', label: t('reconciliationFilters.all'), count: eligibleRows.length },
        ...filters.map((value) => ({
          value, label: filterLabel(value), count: eligibleRows.filter((row) => matches(row, value)).length,
        })),
      ]} />
  </Stack>;
}
